import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { formatApiDetail, getAdminLegalCommercial, updateAdminLegalCommercial } from '../../lib/api';

const TEXT_FIELDS = [
  { key: 'promo_validity_text', label: 'Vigencia de las promociones ({{vigencia_promos}})', placeholder: 'Hasta nuevo aviso' },
  { key: 'invoice_text', label: 'Texto de factura ({{factura}})', placeholder: '' },
  { key: 'rating_discount_terms', label: 'Condiciones del descuento por calificar', placeholder: 'Ej. válido 30 días, un uso, no acumulable' },
  { key: 'claim_window_text', label: 'Plazo sugerido para reclamar ({{plazo_reclamo}})', placeholder: 'el mismo día de la entrega' },
  { key: 'refund_days_text', label: 'Plazo de reembolso ({{plazo_reembolso}})', placeholder: '5 días hábiles' },
  { key: 'cash_on_delivery_methods_text', label: 'Medios aceptados contra entrega (vacío = no se menciona)', placeholder: 'solo efectivo' },
];

/**
 * Condiciones comerciales de los términos (paquete legal v2026-10-09). Componente nuevo para no tocar
 * AdminPizasStore.js (difiere entre servidor y PC). Envío, radio y tiempo se siguen editando en Config tienda.
 */
export default function LegalCommercialCard() {
  const [values, setValues] = useState(null);
  const [formatted, setFormatted] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getAdminLegalCommercial()
      .then((d) => {
        setValues(d.values || {});
        setFormatted(d.formatted || {});
      })
      .catch(() => toast.error('No se pudieron cargar las condiciones comerciales.'));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const d = await updateAdminLegalCommercial({
        ...values,
        min_order_mxn: Number(values.min_order_mxn || 0),
        prices_include_vat: values.prices_include_vat !== false,
      });
      setValues(d.values || values);
      setFormatted(d.formatted || {});
      toast.success('Condiciones comerciales guardadas.');
    } catch (err) {
      toast.error(formatApiDetail(err?.response?.data?.detail, 'No se pudo guardar.'));
    } finally {
      setSaving(false);
    }
  };

  if (!values) return null;

  return (
    <Card className="scroll-mt-24" id="condiciones-comerciales" data-testid="legal-commercial-card">
      <CardHeader>
        <CardTitle>Condiciones comerciales (términos y condiciones)</CardTitle>
        <p className="text-sm text-muted-foreground">
          Se muestran en /terminos, el checkout y WhatsApp. Envío actual: <strong>{formatted.costo_envio || '—'}</strong>
          {' · '}Radio: <strong>{formatted.radio_entrega_km ? `${formatted.radio_entrega_km} km` : '—'}</strong>
          {' · '}Tiempo: <strong>{formatted.tiempo_entrega || '—'}</strong> (se editan en Config tienda).
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={save} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="lc-min">Pedido mínimo (MXN, 0 = sin mínimo)</Label>
              <Input
                id="lc-min"
                type="number"
                min="0"
                step="1"
                value={values.min_order_mxn ?? 0}
                onChange={(e) => setValues({ ...values, min_order_mxn: e.target.value })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm pt-7" htmlFor="lc-vat">
              <input
                id="lc-vat"
                type="checkbox"
                checked={values.prices_include_vat !== false}
                onChange={(e) => setValues({ ...values, prices_include_vat: e.target.checked })}
              />
              Los precios incluyen IVA
            </label>
            {TEXT_FIELDS.map((f) => (
              <div key={f.key} className="space-y-2 sm:col-span-2">
                <Label htmlFor={`lc-${f.key}`}>{f.label}</Label>
                <Input
                  id={`lc-${f.key}`}
                  value={values[f.key] ?? ''}
                  placeholder={f.placeholder}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={saving} className="rounded-full">
              {saving ? 'Guardando…' : 'Guardar condiciones'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
